import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useMemo } from 'react';

import { TLocalesKeys } from '../../../../types';
import { IArticleItem, IArticleSelector } from '../../../common/messageCreators';
import { useUserActionClickSelector } from '../../hooks';
import { useOpenArticleFromChatbot } from '../../hooks/useOpenArticleFromChatbot';
import { MessageItem } from '../MessageItem';
import { SelectorFooter, SelectorWrapper } from '../Selectors';
import { ArticleItem } from './ArticleItem';

export const ArticleSelector: FC<IArticleSelector> = ({ id, type, items }) => {
  const { t } = useTranslation();
  const emit = useUserActionClickSelector();
  const openArticleFromChatbot = useOpenArticleFromChatbot();

  const footerLabel = useMemo(
    () =>
      items.every((item) => item.subtitle)
        ? t<TLocalesKeys>('select_article_finish_extended')
        : t<TLocalesKeys>('select_article_finish'),
    [t, items],
  );

  const onArticleClick = useCallback(
    (item: IArticleItem, index: number) => {
      // if article has subtitle we want to proceed with it in chatbot(send userAction and see this article details in the next message)
      // if no subtitle - just open it in sidebar and skip(parameter to `emit` function) userAction
      const openInSidebar = !item.subtitle;

      if (openInSidebar) {
        openArticleFromChatbot(item._id);
      }

      emit(
        type,
        {
          item,
          label: item.title,
          position: index,
        },
        openInSidebar,
      );
    },
    [openArticleFromChatbot, emit, type],
  );

  const footerActions = useMemo(
    () => [
      {
        label: footerLabel,
        onClick: () => {
          emit(type, {
            label: footerLabel,
            footer: true,
          });
        },
      },
    ],
    [footerLabel, emit, type],
  );

  return (
    <MessageItem id={id} dataHook="article-selector-links">
      <SelectorWrapper>
        {items.map((item, index) => (
          <ArticleItem key={item._id} index={index} article={item} onArticleClick={onArticleClick} />
        ))}
        {footerActions.map(({ label, onClick: onFooterActionClick }) => (
          <SelectorFooter label={label} onClick={onFooterActionClick} />
        ))}
      </SelectorWrapper>
    </MessageItem>
  );
};
